/**
 * Permission model — action classification and approval defaults.
 *
 * Every tool exposed by an MCP server declares an `action_class`. The class
 * drives default approval behavior and audit obligations. The full permission
 * model (allowlists, scoped credentials, audit, dry-run) is laid out in the
 * architecture plan; this file holds the minimal v1 enums and helpers.
 */
import { z } from 'zod';

export const ACTION_CLASSES = ['read', 'write', 'destructive', 'external_effect'] as const;
export type ActionClass = (typeof ACTION_CLASSES)[number];

export const ActionClassSchema = z.enum(ACTION_CLASSES);

/** Default approval requirements per action class. */
export const REQUIRES_APPROVAL_BY_DEFAULT: Readonly<Record<ActionClass, boolean>> = {
  read: false,
  write: true,
  destructive: true,
  external_effect: true,
};

/** Whether the action's approval requirement can be overridden per task. */
export const APPROVAL_OVERRIDABLE: Readonly<Record<ActionClass, boolean>> = {
  read: true, // approval is already false; override is meaningless but allowed
  write: true,
  destructive: false, // never overridable
  external_effect: false, // never overridable
};

/** Which classes are recorded in the audit_actions table. */
export const REQUIRES_AUDIT_ROW: Readonly<Record<ActionClass, boolean>> = {
  read: false,
  write: false,
  destructive: true,
  external_effect: true,
};

export function requiresApproval(
  cls: ActionClass,
  taskOverride: boolean | undefined,
): boolean {
  if (taskOverride !== undefined && APPROVAL_OVERRIDABLE[cls]) {
    return taskOverride;
  }
  return REQUIRES_APPROVAL_BY_DEFAULT[cls];
}

export function requiresAuditRow(cls: ActionClass): boolean {
  return REQUIRES_AUDIT_ROW[cls];
}
